import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Backdrop,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ReactQuill, { Quill } from 'react-quill';
import AceEditor from 'react-ace';
import beautify from 'js-beautify';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-beautify';
import 'react-quill/dist/quill.snow.css';
import '../../styles/pdfEditor.css';
import ToolbarButton, { ToolbarGroup, ToolbarDivider } from './ToolbarButton';
import CustomQuillToolbar from './CustomQuillToolbar';
import ButtonBlot from './ButtonBlot';
import InputBlot from './InputBlot';
import PageBreakBlot from './PageBreakBlot';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import ImageResize from './QuillImageResize';
import { EditorPageManager } from '../../utils/editorPageManager';

// Components
import PDFPreview from './PDFPreview';
import PDFSettings from './PDFSettings';
import PDFCustomCode from './PDFCustomCode';

// Stores
import usePDFStore from '../../stores/pdfStore';
import useNotificationStore from '../../stores/notificationStore';

// Register custom fonts
const Font = Quill.import('formats/font');
Font.whitelist = ['serif', 'monospace', 'cairo', 'roboto', 'opensans', 'lato', 'montserrat', 'poppins', 'tajawal'];
Quill.register(Font, true);

// Register custom sizes
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px'];
Quill.register(Size, true);

// Register font weight as a Style Attributor
const Parchment = Quill.import('parchment');
const WeightStyle = new Parchment.Attributor.Style('weight', 'font-weight', {
  scope: Parchment.Scope.INLINE,
  whitelist: ['100', '200', '300', '400', '500', '600', '700', '800', '900']
});
Quill.register('formats/weight', WeightStyle, true);

// Register image resize module
Quill.register('modules/imageResize', ImageResize);

// Register custom button blot
Quill.register(ButtonBlot);

// Register custom input blot
Quill.register(InputBlot);

// Register page break blot
Quill.register(PageBreakBlot);


// Custom toolbar options for Quill
const toolbarOptions = [
  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
  [{ 'font': ['', 'serif', 'monospace', 'cairo', 'roboto', 'opensans', 'lato', 'montserrat', 'poppins', 'tajawal'] }],
  [{ 'size': ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px'] }],
  
  ['bold', 'italic', 'underline', 'strike'],
  [{ 'color': [] }, { 'background': [] }],
  [{ 'script': 'sub'}, { 'script': 'super' }],
  
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  [{ 'indent': '-1'}, { 'indent': '+1' }],
  [{ 'direction': 'rtl' }],
  [{ 'align': [] }],
  
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  
  ['clean']
];

// Custom clipboard module to preserve styles
const Clipboard = Quill.import('modules/clipboard');

class CustomClipboard extends Clipboard {
  constructor(quill, options) {
    super(quill, options);
    
    // Override the default paste handler
    this.quill.root.addEventListener('paste', this.onCustomPaste.bind(this), true);
  }

  onCustomPaste(e) {
    const range = this.quill.getSelection();
    if (!range) return;

    // Try to get HTML content first
    let html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    if (html) {
      e.preventDefault();
      e.stopPropagation();
      
      // Parse HTML and preserve styles
      const ops = [];
      const temp = document.createElement('div');
      temp.innerHTML = html;
      
      // Pre-process tables to convert to readable format
      this.preprocessTables(temp);
      
      this.parseNode(temp, ops);
      
      // Delete current selection if any
      if (range.length > 0) {
        this.quill.deleteText(range.index, range.length, 'user');
      }
      
      // Insert the parsed content
      let insertIndex = range.index;
      ops.forEach(op => {
        if (op.insert) {
          if (op.attributes && Object.keys(op.attributes).length > 0) {
            this.quill.insertText(insertIndex, op.insert, op.attributes, 'user');
          } else {
            this.quill.insertText(insertIndex, op.insert, 'user');
          }
          insertIndex += op.insert.length;
        }
      });
      
      this.quill.setSelection(insertIndex, 0, 'user');
    }
    // Let default handler handle plain text
  }
  
  preprocessTables(element) {
    // Convert tables to a more readable format
    const tables = element.querySelectorAll('table');
    tables.forEach(table => {
      const div = document.createElement('div');
      const rows = table.querySelectorAll('tr');
      
      rows.forEach((row, rowIndex) => {
        const cells = row.querySelectorAll('td, th');
        const rowDiv = document.createElement('div');
        
        cells.forEach((cell, cellIndex) => {
          const cellSpan = document.createElement('span');
          cellSpan.innerHTML = cell.innerHTML;
          if (cellIndex > 0) {
            rowDiv.appendChild(document.createTextNode(' | '));
          }
          rowDiv.appendChild(cellSpan);
        });
        
        div.appendChild(rowDiv);
      });
      
      table.parentNode.replaceChild(div, table);
    });
  }


  parseNode(node, ops, formats = {}) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent) {
        ops.push({
          insert: node.textContent,
          attributes: { ...formats }
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const newFormats = { ...formats };
      
      // Extract styles from the element
      const styles = this.extractStyles(node);
      Object.assign(newFormats, styles);
      
      // Special handling for lists
      const tagName = node.tagName.toLowerCase();
      if (tagName === 'ul' || tagName === 'ol') {
        // Process list items with proper formatting
        for (const child of node.childNodes) {
          if (child.nodeType === Node.ELEMENT_NODE && child.tagName.toLowerCase() === 'li') {
            const listFormats = { ...newFormats };
            if (tagName === 'ul') listFormats.list = 'bullet';
            else if (tagName === 'ol') listFormats.list = 'ordered';
            
            // Process li content
            for (const liChild of child.childNodes) {
              this.parseNode(liChild, ops, listFormats);
            }
            
            // Add line break after list item
            if (ops.length > 0 && !ops[ops.length - 1].insert.endsWith('\n')) {
              ops.push({ insert: '\n', attributes: listFormats });
            }
          } else {
            this.parseNode(child, ops, newFormats);
          }
        }
      } else {
        // Process children normally
        for (const child of node.childNodes) {
          this.parseNode(child, ops, newFormats);
        }
        
        // Add line break for block elements
        if (this.isBlockElement(node) && ops.length > 0) {
          const lastOp = ops[ops.length - 1];
          if (lastOp.insert && !lastOp.insert.endsWith('\n')) {
            ops.push({ insert: '\n' });
          }
        }
      }
    }
  }

  extractStyles(element) {
    const formats = {};
    
    // Tag-based formatting
    switch (element.tagName.toLowerCase()) {
      case 'b':
      case 'strong':
        formats.bold = true;
        break;
      case 'i':
      case 'em':
        formats.italic = true;
        break;
      case 'u':
        formats.underline = true;
        break;
      case 'strike':
      case 's':
        formats.strike = true;
        break;
      case 'sub':
        formats.script = 'sub';
        break;
      case 'sup':
        formats.script = 'super';
        break;
      case 'h1':
        formats.header = 1;
        break;
      case 'h2':
        formats.header = 2;
        break;
      case 'h3':
        formats.header = 3;
        break;
      case 'h4':
        formats.header = 4;
        break;
      case 'h5':
        formats.header = 5;
        break;
      case 'h6':
        formats.header = 6;
        break;
      case 'a':
        formats.link = element.href;
        break;
      case 'ul':
      case 'ol':
        // Handle list items differently
        break;
      case 'li':
        const parent = element.parentElement;
        if (parent && parent.tagName.toLowerCase() === 'ul') {
          formats.list = 'bullet';
        } else if (parent && parent.tagName.toLowerCase() === 'ol') {
          formats.list = 'ordered';
        }
        break;
      case 'blockquote':
        formats.blockquote = true;
        break;
      case 'code':
        formats.code = true;
        break;
      case 'pre':
        formats['code-block'] = true;
        break;
    }
    
    // Style-based formatting
    const style = element.style;
    if (style) {
      if (style.fontWeight === 'bold' || parseInt(style.fontWeight) >= 700) {
        formats.bold = true;
      }
      if (style.fontStyle === 'italic') {
        formats.italic = true;
      }
      if (style.textDecoration?.includes('underline')) {
        formats.underline = true;
      }
      if (style.textDecoration?.includes('line-through')) {
        formats.strike = true;
      }
      if (style.color && style.color !== 'inherit' && style.color !== 'initial') {
        formats.color = this.normalizeColor(style.color);
      }
      if (style.backgroundColor && style.backgroundColor !== 'inherit' && style.backgroundColor !== 'transparent' && style.backgroundColor !== 'initial') {
        formats.background = this.normalizeColor(style.backgroundColor);
      }
      if (style.fontFamily) {
        const fontFamily = style.fontFamily.replace(/['"]/g, '').split(',')[0].trim().toLowerCase();
        // Map common fonts to available Quill fonts
        const fontMap = {
          'arial': 'sans-serif',
          'helvetica': 'sans-serif',
          'times new roman': 'serif',
          'georgia': 'serif',
          'courier new': 'monospace',
          'courier': 'monospace',
          'verdana': 'sans-serif',
          'tahoma': 'sans-serif',
          'calibri': 'sans-serif',
          'cambria': 'serif'
        };
        
        if (fontMap[fontFamily]) {
          formats.font = fontMap[fontFamily];
        } else if (['serif', 'monospace', 'cairo', 'roboto', 'opensans', 'lato', 'montserrat', 'poppins', 'tajawal'].includes(fontFamily)) {
          formats.font = fontFamily;
        }
      }
      if (style.fontSize) {
        // Convert common font sizes to Quill sizes
        const fontSize = style.fontSize;
        if (fontSize.includes('px')) {
          const px = parseInt(fontSize);
          if (px <= 10) formats.size = 'small';
          else if (px >= 18) formats.size = 'large';
          else if (px >= 32) formats.size = 'huge';
          // Otherwise keep default size
        } else if (fontSize.includes('pt')) {
          const pt = parseInt(fontSize);
          if (pt <= 8) formats.size = 'small';
          else if (pt >= 14) formats.size = 'large';
          else if (pt >= 24) formats.size = 'huge';
        } else {
          formats.size = fontSize;
        }
      }
      if (style.textAlign) {
        formats.align = style.textAlign;
      }
      if (style.direction) {
        formats.direction = style.direction;
      }
    }
    
    return formats;
  }

  isBlockElement(element) {
    const blockTags = ['div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'td', 'th', 'pre', 'table', 'tr'];
    return blockTags.includes(element.tagName.toLowerCase());
  }
  
  normalizeColor(color) {
    // Convert rgb/rgba to hex for better compatibility
    if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match) {
        const [r, g, b] = match.map(Number);
        return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
      }
    }
    return color;
  }
}

// Register the custom clipboard
Quill.register('modules/clipboard', CustomClipboard, true);

// Quill modules configuration
const modules = {
  toolbar: {
    container: '#toolbar' // Reference to custom toolbar
  },
  clipboard: {
    matchVisual: true // This will help preserve some visual formatting
  },
  imageResize: {
    // Image resize module is enabled
  }
};

// Quill formats
const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'direction', 'align',
  'blockquote', 'code-block',
  'link', 'image', 'video', 'button', 'input', 'pagebreak',
  // Image formats
  'width', 'height', 'style',
  'data-align', 'data-float',
  'data-margin-top', 'data-margin-right', 'data-margin-bottom', 'data-margin-left',
  'data-border-width', 'data-border-color', 'data-border-radius',
  'data-shadow', 'alt', 'title', 'data-link'
];

// Format HTML with beautify - defined outside component for reusability
const formatHtml = (html) => {
  if (!html) return '';
  return beautify.html(html, {
    indent_size: 2,
    indent_char: ' ',
    max_preserve_newlines: 1,
    preserve_newlines: true,
    indent_inner_html: true,
    wrap_line_length: 120,
    wrap_attributes: 'auto',
    unformatted: ['code', 'pre'],
    content_unformatted: ['pre'],
    extra_liners: ['head', 'body', '/body', '/html']
  });
};

// Convert page breaks to HTML markers for HTML editor
const convertPageBreaksForHtml = (html) => {
  if (!html) return '';
  
  // Replace complex page break divs with simple HTML comment markers
  let cleanHtml = html.replace(
    /<div[^>]*class="[^"]*page-break[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
    '<!-- PAGE_BREAK -->'
  );
  
  return cleanHtml;
};

// Restore page breaks from HTML markers
const restorePageBreaksFromHtml = (html) => {
  if (!html) return html;
  
  // Replace HTML comment markers with actual page break divs
  let restoredHtml = html.replace(
    /<!--\s*PAGE_BREAK\s*-->/gi,
    '<div class="page-break ql-pagebreak" contenteditable="false" data-page-break="true" data-blot="pagebreak"></div>'
  );
  
  return restoredHtml;
};

// Detect HTML content type
const detectHtmlType = (html) => {
  if (!html) return 'empty';
  
  const trimmed = html.trim();
  
  // Check for full HTML document indicators
  const hasDoctype = /<!DOCTYPE/i.test(trimmed);
  const hasHtmlTag = /<html[^>]*>/i.test(trimmed);
  const hasHeadTag = /<head[^>]*>/i.test(trimmed);
  const hasStyleTag = /<style[^>]*>[\s\S]*?<\/style>/i.test(trimmed);
  const hasBodyTag = /<body[^>]*>/i.test(trimmed);
  const hasComplexStyles = /class=["'][^"']+["']/i.test(trimmed);
  
  if (hasDoctype || (hasHtmlTag && hasHeadTag)) {
    return 'full-document';
  } else if (hasStyleTag || (hasComplexStyles && trimmed.length > 1000)) {
    return 'styled-content';
  } else if (/<[^>]+>/.test(trimmed)) {
    return 'simple-html';
  } else {
    return 'plain-text';
  }
};

// Extract styles from HTML document
const extractStylesFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Extract all style tags
  const styleTags = doc.querySelectorAll('style');
  let styles = '';
  
  styleTags.forEach(tag => {
    styles += tag.textContent + '\n\n';
  });
  
  // Extract link tags for external stylesheets (as comments)
  const linkTags = doc.querySelectorAll('link[rel="stylesheet"]');
  if (linkTags.length > 0) {
    styles += '/* External stylesheets referenced in original document:\n';
    linkTags.forEach(link => {
      styles += ` * ${link.href}\n`;
    });
    styles += ' */\n\n';
  }
  
  // Extract inline styles from body as a reference
  const elementsWithInlineStyles = doc.querySelectorAll('[style]');
  if (elementsWithInlineStyles.length > 0) {
    styles += '/* Inline styles found in document:\n';
    elementsWithInlineStyles.forEach(el => {
      const selector = el.tagName.toLowerCase() + 
        (el.id ? `#${el.id}` : '') + 
        (el.className ? `.${el.className.split(' ').join('.')}` : '');
      styles += ` * ${selector}: ${el.getAttribute('style')}\n`;
    });
    styles += ' */\n';
  }
  
  return styles;
};

// Extract body content preserving structure
const extractBodyContent = (html, preserveStyles = false) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Get body content or full content if no body tag
  const bodyElement = doc.body;
  if (!bodyElement) return html;
  
  // Remove script tags for security
  const scripts = bodyElement.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  
  // Only remove style tags if not preserving
  if (!preserveStyles) {
    const styles = bodyElement.querySelectorAll('style');
    styles.forEach(style => style.remove());
  }
  
  return bodyElement.innerHTML;
};

// Process pasted HTML to fit page constraints
const processHtmlForPages = (html, pageHeightPx = 971) => {
  if (!html) return html;
  
  // Create a temporary container to measure content
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.width = '794px'; // A4 width in pixels
  tempDiv.innerHTML = html;
  document.body.appendChild(tempDiv);
  
  // Get all block elements
  const elements = tempDiv.querySelectorAll('p, div:not(.page-break), h1, h2, h3, h4, h5, h6, ul, ol, table, blockquote, pre, section, article');
  let currentHeight = 0;
  let pageBreakIndices = [];
  
  elements.forEach((element, index) => {
    const elementHeight = element.offsetHeight;
    
    // Check if adding this element would exceed page height
    if (currentHeight + elementHeight > pageHeightPx && currentHeight > 0) {
      // Mark this position for page break
      pageBreakIndices.push(index);
      currentHeight = elementHeight;
    } else {
      currentHeight += elementHeight;
    }
  });
  
  // Remove temp container
  document.body.removeChild(tempDiv);
  
  // Insert page breaks at marked positions
  if (pageBreakIndices.length > 0) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const bodyElements = doc.body.querySelectorAll('p, div:not(.page-break), h1, h2, h3, h4, h5, h6, ul, ol, table, blockquote, pre, section, article');
    
    // Insert page breaks in reverse order to maintain indices
    for (let i = pageBreakIndices.length - 1; i >= 0; i--) {
      const index = pageBreakIndices[i];
      if (bodyElements[index]) {
        const pageBreak = doc.createElement('div');
        pageBreak.innerHTML = '<!-- PAGE_BREAK -->';
        bodyElements[index].parentNode.insertBefore(pageBreak.firstChild, bodyElements[index]);
      }
    }
    
    return doc.body.innerHTML;
  }
  
  return html;
};

const PDFEditor = ({ isNew = false, onClose, onSave }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const quillRef = useRef(null);
  const aceEditorRef = useRef(null);
  const editorStyleRef = useRef(null);
  const themeStyleRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const {
    currentPdf,
    categories,
    loading,
    fetchPDF,
    fetchCategories,
    createPDF,
    updatePDF,
    generatePDF
  } = usePDFStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    customCSS: '',
    customJS: '',
    status: 'draft',
    categories: [],
    tags: [],
    config: {
      pdfType: 'simple',
      pageSize: 'A4',
      orientation: 'portrait',
      margins: { top: 20, right: 20, bottom: 20, left: 20 },
      headerFooter: {
        enabled: false,
        header: {
          left: '',
          center: '',
          right: '',
          fontSize: '10pt'
        },
        footer: {
          left: '',
          center: '',
          right: '',
          fontSize: '10pt'  
        }
      },
      pageNumbers: false,
      watermark: {
        enabled: false,
        text: '',
        image: '',
        position: 'center',
        opacity: 0.3,
        downloadOnly: false,
        textSize: 60,
        imageScale: 0.5,
        color: 'rgba(0, 0, 0, 0.3)',
        rotation: 0,
        pattern: {
          enabled: false,
          spacing: {
            horizontal: 150,
            vertical: 150
          },
          density: 'medium'
        },
        fontFamily: 'Arial',
        fontStyle: {
          bold: true,
          italic: false
        },
        textTransform: 'none',
        border: {
          enabled: false,
          width: 2,
          color: '#ffffff'
        },
        blendMode: 'normal',
        customPosition: {
          x: 50,
          y: 50
        }
      },
      security: {
        password: '',
        expiresAt: null
      },
      seo: {
        metaDescription: '',
        indexable: true
      },
      preview: {
        mode: 'split',
        autoRefresh: true
      },
      branding: {
        showLogo: true,
        customText: ''
      },
      jsEnabled: false
    },
    isPublic: false
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState(formData.config?.preview?.mode || 'split');
  const [showPreview, setShowPreview] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pageManager, setPageManager] = useState(null);
  const [currentPageInfo, setCurrentPageInfo] = useState({ currentPage: 1, totalPages: 1 });
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'html'
  const [htmlContent, setHtmlContent] = useState('');
  const [pasteOptions, setPasteOptions] = useState({
    preserveFormatting: false,
    processForPages: true,
    extractStyles: true
  });
  const [hasExtractedStyles, setHasExtractedStyles] = useState(false);
  const [preservedHtml, setPreservedHtml] = useState(null); // Store original HTML when preserve is used
  const [isPreservedMode, setIsPreservedMode] = useState(false); // Track if we're in preserved HTML mode
  const [showPreservedWarning, setShowPreservedWarning] = useState(false); // Show warning about preserved HTML
  
  // Unsaved changes dialog state
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Load PDF data if editing and fetch categories
  useEffect(() => {
    // Always fetch categories
    fetchCategories();
    
    if (!isNew && id) {
      fetchPDF(id).then(pdf => {
        if (pdf) {
          setFormData({
            ...pdf,
            categories: pdf.categories?.map(c => c._id) || [],
            tags: pdf.tags || []
          });
          // Initialize HTML content with formatted version and cleaned page breaks
          if (pdf.content) {
            const cleanedHtml = convertPageBreaksForHtml(pdf.content);
            setHtmlContent(formatHtml(cleanedHtml));
          }
        }
      });
    }
  }, [id, isNew]);
  
  // Handle form change
  const handleChange = (field, value) => {
    setHasUnsavedChanges(true);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle config change
  const handleConfigChange = (field, value) => {
    setHasUnsavedChanges(true);
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };
  
  // Handle content change
  const handleContentChange = (content) => {
    console.log('Editor content changed:', content);
    console.log('Contains page-break:', content.includes('page-break'));
    setHasUnsavedChanges(true);
    
    // Don't process if we're in preserved mode and visual editor
    if (isPreservedMode && editorMode === 'visual') {
      // User shouldn't be able to edit in visual mode when preserved
      return;
    }
    
    // Simply update the content
    handleChange('content', content);
    
    // If we're in HTML mode, update the HTML content as well to keep it in sync
    if (editorMode === 'html' && !isPreservedMode) {
      const cleanedHtml = convertPageBreaksForHtml(content);
      const formatted = formatHtml(cleanedHtml);
      setHtmlContent(formatted);
    }
    
    // Update page info if page manager exists
    if (pageManager) {
      const currentPage = pageManager.getCurrentPage();
      const totalPages = pageManager.countPages();
      setCurrentPageInfo({ currentPage, totalPages });
    }
  };
  
  // Ensure images are properly initialized when content is loaded
  useEffect(() => {
    if (formData.content && quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Give the editor time to render the content
      const initializeImages = () => {
        const imageModule = quill.getModule('imageResize');
        if (imageModule && imageModule.initializeExistingImages) {
          imageModule.initializeExistingImages();
        }
      };
      
      // Try multiple times to ensure content is fully rendered
      setTimeout(initializeImages, 100);
      setTimeout(initializeImages, 300);
      setTimeout(initializeImages, 500);
    }
  }, [formData.content, id]); // Also trigger when id changes (page load)
  
  // Initialize page manager and update when config changes
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      
      // Determine page height based on page size
      let pageHeight = 297; // A4 default
      if (formData.config?.pageSize === 'Letter') {
        pageHeight = 279;
      } else if (formData.config?.pageSize === 'Legal') {
        pageHeight = 356;
      }
      
      // Handle orientation swap
      let actualPageHeight = pageHeight;
      if (formData.config?.orientation === 'landscape') {
        // In landscape, height and width are swapped
        const pageWidths = { A4: 210, Letter: 216, Legal: 216 };
        actualPageHeight = pageWidths[formData.config?.pageSize || 'A4'];
      }
      
      const manager = new EditorPageManager(quill, {
        pageHeight: actualPageHeight,
        marginTop: formData.config?.margins?.top || 20,
        marginBottom: formData.config?.margins?.bottom || 20
      });
      
      manager.init();
      setPageManager(manager);
      
      // Listen for page updates
      const handlePagesUpdated = (event) => {
        console.log('Pages updated event:', event.detail);
        setCurrentPageInfo({
          currentPage: event.detail.currentPage || 1,
          totalPages: event.detail.totalPages || 1
        });
      };
      quill.root.addEventListener('pages-updated', handlePagesUpdated);
      
      // Also listen for selection changes to update current page and cursor position
      const handleSelectionChange = (range) => {
        if (manager && range) {
          const currentPage = manager.getCurrentPage();
          const totalPages = manager.countPages();
          
          // Get cursor position within the editor content
          const cursorBounds = quill.getBounds(range.index, range.length);
          
          // Get editor's scroll position to calculate absolute position in content
          const editorElement = quill.root;
          const editorScrollTop = editorElement.scrollTop || 0;
          
          // Calculate cursor's absolute position in the content (not just viewport)
          const cursorAbsoluteInContent = cursorBounds.top + editorScrollTop;
          
          // Find the cursor position within its page by analyzing page breaks
          let cursorPositionInPage = cursorAbsoluteInContent;
          let pageStartOffset = 0;
          
          // Get all elements up to the cursor to find the last page break
          const elements = Array.from(quill.root.children);
          let lastPageBreakOffset = 0;
          
          for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            const elementTop = element.offsetTop;
            
            // Stop if we've passed the cursor
            if (elementTop > cursorAbsoluteInContent) {
              break;
            }
            
            // Check if this is a page break (manual or automatic)
            if (manager.isPageBreak(element)) {
              // Record the position after this page break as the start of the new page
              lastPageBreakOffset = elementTop + element.offsetHeight;
            }
          }
          
          // Calculate position relative to the start of the current page
          cursorPositionInPage = cursorAbsoluteInContent - lastPageBreakOffset;
          
          // For automatic page breaks without manual breaks, also check page boundaries
          if (lastPageBreakOffset === 0) {
            // No manual page break found, check if we're on an auto-generated page
            const pageHeight = manager.maxContentHeightPx || 971;
            const autoPageNumber = Math.floor(cursorAbsoluteInContent / pageHeight);
            if (autoPageNumber > 0) {
              cursorPositionInPage = cursorAbsoluteInContent % pageHeight;
            }
          }
          
          setCurrentPageInfo({ 
            currentPage, 
            totalPages,
            cursorPosition: {
              index: range.index,
              length: range.length,
              absoluteInContent: cursorAbsoluteInContent,
              page: currentPage, // This is already correct from manager.getCurrentPage()
              positionInPage: cursorPositionInPage,
              pageStartOffset: lastPageBreakOffset,
              relativeTop: cursorBounds.top,
              scrollTop: editorScrollTop,
              left: cursorBounds.left,
              height: cursorBounds.height
            }
          });
        }
      };
      quill.on('selection-change', handleSelectionChange);
      quill.on('text-change', () => {
        const range = quill.getSelection();
        if (range) handleSelectionChange(range);
      });
      
      // Listen for page break delete events from the delete button
      quill.on('pagebreak-delete-start', () => {
        manager.isDeletingPageBreak = true;
      });
      
      quill.on('pagebreak-delete-end', () => {
        manager.isDeletingPageBreak = false;
        // Check for page breaks after deletion is complete
        setTimeout(() => manager.checkAndInsertPageBreaks(), 50);
      });
      
      // Add keyboard support for deleting page breaks
      const handleKeyboard = (range, context) => {
        if (!range) return true;
        
        // Check if we're at a page break
        const [blot] = quill.getLeaf(range.index);
        const nextBlot = blot?.next;
        const prevBlot = blot?.prev;
        
        // Handle Backspace at the beginning of content after a page break
        if (context.key === 'Backspace' && range.index > 0 && range.length === 0) {
          const [prevLeaf] = quill.getLeaf(range.index - 1);
          if (prevLeaf && prevLeaf.parent && prevLeaf.parent.domNode?.classList?.contains('ql-pagebreak')) {
            // Temporarily disable page checking while removing the page break
            manager.isDeletingPageBreak = true;
            
            // Get the page break index and remove it
            const pageBreakIndex = quill.getIndex(prevLeaf.parent);
            quill.deleteText(pageBreakIndex, 1, 'silent');
            
            // Set cursor to the correct position after deletion
            quill.setSelection(pageBreakIndex, 0, 'silent');
            
            // Re-enable page checking after a brief delay to allow content to reflow
            setTimeout(() => {
              manager.isDeletingPageBreak = false;
              // Then check for page breaks after content has settled
              setTimeout(() => manager.checkAndInsertPageBreaks(), 50);
            }, 200);
            
            return false;
          }
        }
        
        // Handle Delete when cursor is right before a page break
        if (context.key === 'Delete' && range.length === 0) {
          if (nextBlot && nextBlot.domNode?.classList?.contains('ql-pagebreak')) {
            // Temporarily disable page checking while removing the page break
            manager.isDeletingPageBreak = true;
            
            // Store the current index
            const currentIndex = range.index;
            
            // Get the page break index and remove it
            const pageBreakIndex = quill.getIndex(nextBlot);
            quill.deleteText(pageBreakIndex, 1, 'silent');
            
            // Keep cursor at the same position
            quill.setSelection(currentIndex, 0, 'silent');
            
            // Re-enable page checking after a brief delay to allow content to reflow
            setTimeout(() => {
              manager.isDeletingPageBreak = false;
              // Then check for page breaks after content has settled
              setTimeout(() => manager.checkAndInsertPageBreaks(), 50);
            }, 200);
            
            return false;
          }
        }
        
        return true;
      };
      
      // Add keyboard bindings with proper context to prevent default behavior
      quill.keyboard.addBinding({
        key: 'Backspace',
        handler: handleKeyboard
      });
      
      quill.keyboard.addBinding({
        key: 'Delete',
        handler: handleKeyboard
      });
      
      // Trigger immediate check after config change
      setTimeout(() => manager.refresh(), 100);
      
      return () => {
        // Cleanup
        quill.root.removeEventListener('pages-updated', handlePagesUpdated);
        quill.off('selection-change', handleSelectionChange);
        quill.off('text-change');
        quill.off('pagebreak-delete-start');
        quill.off('pagebreak-delete-end');
        if (manager.checkTimeout) {
          clearTimeout(manager.checkTimeout);
        }
      };
    }
  }, [quillRef.current, formData.config?.pageSize, formData.config?.orientation, formData.config?.margins]);
  
  // Apply custom CSS to editor
  useEffect(() => {
    if (!editorStyleRef.current) {
      editorStyleRef.current = document.createElement('style');
      editorStyleRef.current.id = 'custom-editor-styles';
      document.head.appendChild(editorStyleRef.current);
    }
    
    // Update the style content
    editorStyleRef.current.textContent = formData.customCSS || '';
    
    // Cleanup on unmount
    return () => {
      if (editorStyleRef.current) {
        document.head.removeChild(editorStyleRef.current);
        editorStyleRef.current = null;
      }
    };
  }, [formData.customCSS]);


  // Apply theme-specific overrides for Quill
  useEffect(() => {
    if (!themeStyleRef.current) {
      themeStyleRef.current = document.createElement('style');
      themeStyleRef.current.id = 'quill-theme-overrides';
      document.head.appendChild(themeStyleRef.current);
    }
    
    // Override Quill's default white background and style dropdowns
    themeStyleRef.current.textContent = `
      .ql-container.ql-snow {
        background-color: ${theme.palette.background.paper} !important;
        border-color: ${theme.palette.divider} !important;
        color: ${theme.palette.text.primary} !important;
      }
      .ql-editor {
        background-color: ${theme.palette.background.paper} !important;
        color: ${theme.palette.text.primary} !important;
      }
      .ql-editor.ql-blank::before {
        color: ${theme.palette.text.secondary} !important;
      }
      .ql-snow .ql-stroke {
        stroke: ${theme.palette.text.primary} !important;
      }
      .ql-snow .ql-fill {
        fill: ${theme.palette.text.primary} !important;
      }
      
      /* Dropdown styling to match MUI */
      .ql-snow .ql-picker {
        color: ${theme.palette.text.primary} !important;
        background-color: ${theme.palette.background.default} !important;
      }
      
      .ql-snow .ql-picker-options {
        background-color: ${theme.palette.background.paper} !important;
        border: 1px solid ${theme.palette.divider} !important;
        box-shadow: ${theme.shadows[8]} !important;
        border-radius: 4px !important;
      }
      
      .ql-snow .ql-picker-label {
        color: ${theme.palette.text.primary} !important;
        padding: 6px 8px !important;
        border: 1px solid ${theme.palette.divider} !important;
        border-radius: 4px !important;
        background-color: ${theme.palette.background.default} !important;
      }
      
      .ql-snow .ql-picker-label:hover {
        border-color: ${theme.palette.text.primary} !important;
      }
      
      .ql-snow.ql-toolbar button:hover,
      .ql-snow .ql-toolbar button:hover {
        background-color: ${theme.palette.action.hover} !important;
      }
      
      .ql-snow.ql-toolbar button.ql-active,
      .ql-snow .ql-toolbar button.ql-active {
        background-color: ${theme.palette.primary.main} !important;
        color: ${theme.palette.primary.contrastText} !important;
      }
      
      .ql-snow .ql-picker-item {
        color: ${theme.palette.text.primary} !important;
        padding: 8px 16px !important;
      }
      
      .ql-snow .ql-picker-item:hover {
        background-color: ${theme.palette.action.hover} !important;
        color: ${theme.palette.text.primary} !important;
      }
      
      .ql-snow .ql-picker-item.ql-selected {
        background-color: ${theme.palette.primary.main} !important;
        color: ${theme.palette.primary.contrastText} !important;
      }
      
      /* Fix picker arrow icon */
      .ql-snow .ql-picker-label svg {
        fill: ${theme.palette.text.primary} !important;
        stroke: ${theme.palette.text.primary} !important;
      }
      
      /* Custom button blot styling */
      .ql-custom-button {
        margin: 16px 0;
        padding: 0;
        min-height: 40px;
        position: relative;
      }
      
      .ql-custom-button a {
        font-family: inherit !important;
      }
      
      /* Ensure buttons in editor are selectable but not editable */
      .ql-editor .ql-custom-button {
        user-select: all;
        cursor: pointer;
        position: relative;
        transition: all 0.2s ease;
      }
      
      .ql-editor .ql-custom-button:hover {
        outline: 2px dashed ${theme.palette.primary.main};
        outline-offset: 4px;
      }
      
      .ql-editor .ql-custom-button:hover::after {
        content: 'Double-click to edit';
        position: absolute;
        top: -25px;
        left: 50%;
        transform: translateX(-50%);
        background: ${theme.palette.grey[900]};
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        pointer-events: none;
        z-index: 1000;
      }
      
      .ql-editor .ql-custom-button a {
        pointer-events: none; /* Disable clicks in editor */
        user-select: none;
      }
      
      /* Example custom button styles that can be used with CSS classes */
      .btn-primary {
        background-color: ${theme.palette.primary.main} !important;
        color: ${theme.palette.primary.contrastText} !important;
      }
      
      .btn-secondary {
        background-color: ${theme.palette.secondary.main} !important;
        color: ${theme.palette.secondary.contrastText} !important;
      }
      
      .btn-success {
        background-color: ${theme.palette.success.main} !important;
        color: white !important;
      }
      
      .btn-warning {
        background-color: ${theme.palette.warning.main} !important;
        color: ${theme.palette.warning.contrastText} !important;
      }
      
      .btn-lg {
        padding: 12px 32px !important;
        font-size: 18px !important;
      }
      
      .btn-rounded {
        border-radius: 50px !important;
      }
      
      .btn-shadow {
        box-shadow: 0 4px 6px rgba(0,0,0,0.1) !important;
      }
      
      .btn-gradient {
        background: linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}) !important;
        color: white !important;
      }
      
      /* Selected button highlight */
      .ql-editor .ql-custom-button.ql-selected {
        outline: 2px solid ${theme.palette.primary.main};
        outline-offset: 4px;
      }
      
      /* Custom input blot styling */
      .ql-custom-input {
        margin: 16px 0;
        padding: 0;
        position: relative;
      }
      
      .ql-custom-input .input-wrapper {
        display: inline-block;
      }
      
      .ql-custom-input .input-label {
        display: block;
        margin-bottom: 4px;
        font-size: 14px;
        font-weight: 500;
        color: ${theme.palette.text.primary};
      }
      
      .ql-custom-input input {
        font-family: inherit !important;
        outline: none;
      }
      
      /* Input field styles in editor */
      .ql-editor .ql-custom-input {
        user-select: all;
        cursor: pointer;
        position: relative;
        transition: all 0.2s ease;
      }
      
      .ql-editor .ql-custom-input:hover {
        outline: 2px dashed ${theme.palette.primary.main};
        outline-offset: 4px;
      }
      
      .ql-editor .ql-custom-input:hover::after {
        content: 'Double-click to edit';
        position: absolute;
        top: -25px;
        left: 50%;
        transform: translateX(-50%);
        background: ${theme.palette.grey[900]};
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        pointer-events: none;
        z-index: 1000;
      }
      
      /* Selected input highlight */
      .ql-editor .ql-custom-input.ql-selected {
        outline: 2px solid ${theme.palette.primary.main};
        outline-offset: 4px;
      }
    `;
    
    return () => {
      if (themeStyleRef.current && themeStyleRef.current.parentNode) {
        document.head.removeChild(themeStyleRef.current);
        themeStyleRef.current = null;
      }
    };
  }, [theme]);
  
  // Handle save
  const handleSave = async () => {
    if (!formData.title.trim()) {
      showError('Title is required');
      return;
    }
    
    setSaving(true);
    try {
      let savedPdf;
      if (isNew) {
        savedPdf = await createPDF(formData);
        showSuccess('PDF created successfully');
      } else {
        savedPdf = await updatePDF(id, formData);
        showSuccess('PDF updated successfully');
      }
      
      setHasUnsavedChanges(false);
      
      if (onSave) {
        onSave(savedPdf);
      } else if (isNew) {
        navigate(`/pdf/edit/${savedPdf._id}`);
      }
    } catch (error) {
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle generate PDF
  const handleGeneratePDF = async () => {
    // Save first if there are unsaved changes
    if (hasUnsavedChanges) {
      await handleSave();
    }
    
    setGenerating(true);
    try {
      const result = await generatePDF(currentPdf?._id || formData._id);
      showSuccess('PDF generated successfully');
      
      // Open in new tab
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (error) {
      showError('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };
  
  // Handle close
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingNavigation(() => () => {
        setHasUnsavedChanges(false);
        if (onClose) {
          onClose();
        } else {
          navigate('/pdf');
        }
      });
    } else {
      if (onClose) {
        onClose();
      } else {
        navigate('/pdf');
      }
    }
  };
  
  // Handle preview toggle
  const togglePreview = () => {
    if (viewMode === 'toggle') {
      setShowPreview(!showPreview);
    }
  };
  
  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode) {
      setViewMode(newMode);
      handleConfigChange('preview', { ...formData.config.preview, mode: newMode });
    }
  };
  
  // Handle tag add
  const handleAddTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      handleChange('tags', [...formData.tags, tag]);
    }
  };
  
  // Handle tag remove
  const handleRemoveTag = (tagToRemove) => {
    handleChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };
  
  // Handle editor mode switch
  const handleEditorModeChange = (newMode) => {
    if (newMode === editorMode) return;
    
    if (newMode === 'html') {
      // Convert page breaks to HTML comments and format
      if (isPreservedMode && preservedHtml) {
        // Use preserved HTML if in preserved mode
        const cleanedHtml = convertPageBreaksForHtml(preservedHtml);
        const formatted = formatHtml(cleanedHtml);
        setHtmlContent(formatted);
        setShowPreservedWarning(false);
      } else {
        const cleanedHtml = convertPageBreaksForHtml(formData.content || '');
        const formatted = formatHtml(cleanedHtml);
        setHtmlContent(formatted);
        setShowPreservedWarning(false);
      }
    } else {
      // Switching to Visual mode
      if (isPreservedMode) {
        // Show warning that visual mode can't display complex HTML
        setShowPreservedWarning(true);
        // Keep the editor read-only in preserved mode
        setEditorMode(newMode);
        return;
      } else {
        // Normal mode - restore page breaks and save HTML content back to formData
        if (htmlContent) {
          const restoredHtml = restorePageBreaksFromHtml(htmlContent);
          if (restoredHtml !== formData.content) {
            handleChange('content', restoredHtml);
            
            // If we have preserved formatting with styles, make sure Quill renders them
            if (pasteOptions.preserveFormatting && quillRef.current) {
              // Force Quill to re-render with the new HTML
              setTimeout(() => {
                const quill = quillRef.current.getEditor();
                quill.clipboard.dangerouslyPasteHTML(restoredHtml);
              }, 100);
            }
          }
        }
      }
    }
    
    setEditorMode(newMode);
  };
  
  // Handle HTML content change
  const handleHtmlChange = (newHtml) => {
    setHtmlContent(newHtml);
    // Restore page breaks before updating formData
    const restoredHtml = restorePageBreaksFromHtml(newHtml);
    handleChange('content', restoredHtml);
    setHasUnsavedChanges(true);
    // If in preserved mode, update preserved HTML
    if (isPreservedMode) {
      setPreservedHtml(restoredHtml);
    }
  };
  
  // Note: Custom styles are now handled in pdfEditor.css for better theming
  
  // Add keyboard shortcut handler for fullscreen
  useEffect(() => {
    const handleKeyPress = (e) => {
      // F11 or Cmd/Ctrl + Shift + F for fullscreen
      if (e.key === 'F11' || (e.key === 'f' && e.shiftKey && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setFullscreen(!fullscreen);
      }
      // ESC to exit fullscreen
      if (e.key === 'Escape' && fullscreen) {
        setFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [fullscreen]);

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => 
        hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
      [hasUnsavedChanges]
    )
  );

  // Handle navigation blocking
  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowUnsavedDialog(true);
      setPendingNavigation(() => blocker.proceed);
    }
  }, [blocker]);

  // Handle browser/tab close warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle save and continue navigation
  const handleSaveAndNavigate = async () => {
    await handleSave();
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  // Handle discard and navigate
  const handleDiscardAndNavigate = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  // Handle cancel navigation
  const handleCancelNavigation = () => {
    setShowUnsavedDialog(false);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setPendingNavigation(null);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: fullscreen ? '100vh' : '100%',
      bgcolor: 'background.default',
      position: fullscreen ? 'fixed' : 'relative',
      top: fullscreen ? 0 : 'auto',
      left: fullscreen ? 0 : 'auto',
      right: fullscreen ? 0 : 'auto',
      bottom: fullscreen ? 0 : 'auto',
      zIndex: fullscreen ? 1300 : 'auto',
      transition: 'all 0.3s ease-in-out'
    }}>
      {/* Ultra Compact Header - Hidden in fullscreen */}
      {!fullscreen && (
        <Paper sx={{ 
          py: 0.5, 
          px: 1.5, 
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          transition: 'all 0.3s ease-in-out'
        }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:2}}>
            {/* Title Field - Inline */}
            <TextField
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Untitled Document"
              variant="outlined"
              size="small"
              sx={{ 
                // width: 180,
                flex:1,
                '& .MuiOutlinedInput-root': {
                  height: 28,
                  fontSize: '0.85rem',
                  borderRadius:0.5,
                  '& fieldset': {
                    borderColor: alpha(theme.palette.divider, 0.3),
                  },
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.5,
                  fontSize: '0.85rem',
                }
              }}
            />
            
            {/* Status and Category in same row */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                sx={{ height: 26, fontSize: '0.75rem',borderRadius:0.5, }}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150, maxWidth: 250 }}>
              <Select
                multiple
                value={formData.categories}
                onChange={(e) => handleChange('categories', e.target.value)}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <Typography variant="body2" color="text.secondary">Categories</Typography>;
                  }
                  return `${selected.length} selected`;
                }}
                sx={{ height: 26, fontSize: '0.75rem',borderRadius:0.5, }}
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    <Checkbox checked={formData.categories.indexOf(category._id) > -1} />
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Unsaved changes indicator - Dot */}
            {hasUnsavedChanges && (
              <Tooltip title="Unsaved changes" arrow>
                <Box 
                  sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: 'warning.main',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    }
                  }} 
                />
              </Tooltip>
            )}
            
            
            {/* View Mode Toggle - Compact */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  height: 26,
                  px: 0.75,
                  fontSize: '0.7rem',
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                }
              }}
            >
              <ToggleButton value="toggle">Toggle</ToggleButton>
              <ToggleButton value="split">Split</ToggleButton>
            </ToggleButtonGroup>
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {viewMode === 'toggle' && (
                <IconButton
                  size="small"
                  onClick={togglePreview}
                  color={showPreview ? "primary" : "default"}
                  sx={{ p: 0.5 }}
                >
                  <PreviewIcon fontSize="small" />
                </IconButton>
              )}
              
              <IconButton
                size="small"
                onClick={() => setFullscreen(!fullscreen)}
                sx={{ p: 0.5 }}
                title="Enter Fullscreen (F11)"
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
              
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ 
                  height: 26, 
                  px: 1.25, 
                  fontSize: '0.7rem',
                  minWidth: 60,
                  textTransform: 'none'
                }}
              >
                Save
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                onClick={handleGeneratePDF}
                disabled={generating || !currentPdf?._id}
                sx={{ 
                  height: 26, 
                  px: 1, 
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  borderColor: alpha(theme.palette.divider, 0.3)
                }}
              >
                Generate PDF
              </Button>
              
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}
      
      {/* Fullscreen Top Bar - Fixed position, not floating */}
      {fullscreen && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            zIndex: 1200,
          }}
        >
          {/* Left side - Document info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
              {formData.title || 'Untitled Document'}
            </Typography>
            {hasUnsavedChanges && (
              <Chip 
                label="Unsaved" 
                size="small" 
                color="warning"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          
          {/* Right side - Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Preview toggle for toggle mode */}
            {viewMode === 'toggle' && (
              <Button
                size="small"
                variant="text"
                startIcon={<PreviewIcon fontSize="small" />}
                onClick={togglePreview}
                sx={{ 
                  height: 28,
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                {showPreview ? "Editor" : "Preview"}
              </Button>
            )}
            
            {/* Save button */}
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={12} /> : <SaveIcon fontSize="small" />}
              onClick={handleSave}
              disabled={saving}
              sx={{ 
                height: 28,
                textTransform: 'none',
                fontSize: '0.75rem'
              }}
            >
              Save
            </Button>
            
            {/* Exit fullscreen */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<FullscreenExitIcon fontSize="small" />}
              onClick={() => setFullscreen(false)}
              sx={{ 
                height: 28,
                textTransform: 'none',
                fontSize: '0.75rem'
              }}
            >
              Exit
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Tabs - Compact and Hidden in fullscreen */}
      {!fullscreen && (
        <Paper sx={{ borderRadius: 0, borderBottom: `1px solid ${theme.palette.divider}` }} elevation={0}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                minHeight: 32,
                py: 0.25,
                px: 2,
                fontSize: '0.75rem',
                textTransform: 'none'
              }
            }}
          >
            <Tab label="Content" />
            <Tab label="Custom Code" />
            <Tab label="Settings" />
          </Tabs>
        </Paper>
      )}
      
      {/* Content Area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Editor */}
        <Box sx={{ 
          flex: viewMode === 'split' || !showPreview ? 1 : 0,
          display: viewMode === 'toggle' && showPreview ? 'none' : 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ 
            height: '100%', 
            display: activeTab === 0 ? 'flex' : 'none',
            flexDirection: 'column',
            position: 'relative',
          }}>
              {/* Editor Header Bar - Similar to Preview header */}
              <Box sx={{ 
                height: 32,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                  Editor
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {editorMode === 'visual' && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      Page {currentPageInfo.currentPage} of {currentPageInfo.totalPages}
                    </Typography>
                  )}
                  <ToggleButtonGroup
                    value={editorMode}
                    exclusive
                    onChange={(e, newMode) => newMode && handleEditorModeChange(newMode)}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        height: 22,
                        px: 0.75,
                        fontSize: '0.7rem',
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      }
                    }}
                  >
                    <ToggleButton value="visual">Visual</ToggleButton>
                    <ToggleButton value="html">HTML</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              
              {/* Toolbar */}
              <Box sx={{ 
                position: 'relative',
                top: 0,
                zIndex: 10,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  {editorMode === 'visual' ? (
                    <Box sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      overflow: 'hidden',
                      width: '100%',
                      minWidth: 0, // Important for flex children
                    }}>
                      <CustomQuillToolbar 
                        quillRef={quillRef} 
                        activeFormats={{}} 
                        pdfType={formData.config?.pdfType || 'simple'}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        HTML Editor
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const formatted = formatHtml(htmlContent);
                          setHtmlContent(formatted);
                          const restoredHtml = restorePageBreaksFromHtml(formatted);
                          handleChange('content', restoredHtml);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Format HTML
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          // Process current content for pages
                          const pageHeight = pageManager?.maxContentHeightPx || 971;
                          const processed = processHtmlForPages(htmlContent, pageHeight);
                          const formatted = formatHtml(processed);
                          setHtmlContent(formatted);
                          const restoredHtml = restorePageBreaksFromHtml(formatted);
                          handleChange('content', restoredHtml);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Add Page Breaks
                      </Button>
                      <Divider orientation="vertical" flexItem />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={pasteOptions.preserveFormatting}
                            onChange={(e) => setPasteOptions(prev => ({ 
                              ...prev, 
                              preserveFormatting: e.target.checked,
                              processForPages: !e.target.checked // Disable page processing if preserving
                            }))}
                          />
                        }
                        label={<Typography variant="caption">Preserve Original</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={pasteOptions.processForPages}
                            disabled={pasteOptions.preserveFormatting}
                            onChange={(e) => setPasteOptions(prev => ({ ...prev, processForPages: e.target.checked }))}
                          />
                        }
                        label={<Typography variant="caption">Auto Page Breaks</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={pasteOptions.extractStyles}
                            onChange={(e) => setPasteOptions(prev => ({ ...prev, extractStyles: e.target.checked }))}
                          />
                        }
                        label={<Typography variant="caption">Extract Styles</Typography>}
                      />
                      {hasExtractedStyles && (
                        <Chip 
                          label="Styles extracted to CSS tab" 
                          size="small" 
                          color="success" 
                          onDelete={() => setHasExtractedStyles(false)}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              <Box 
                sx={{ 
                  flex: 1,
                  overflow: 'auto',
                  overscrollBehavior: 'contain',
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  '& > div': {
                    width: '100%',
                    maxWidth: (() => {
                      // Calculate editor width based on page settings
                      const pageSizes = {
                        A4: { width: 210, height: 297 },
                        Letter: { width: 216, height: 279 },
                        Legal: { width: 216, height: 356 }
                      };
                      const pageSize = formData.config?.pageSize || 'A4';
                      const orientation = formData.config?.orientation || 'portrait';
                      const margins = formData.config?.margins || {};
                      
                      const dimensions = pageSizes[pageSize] || pageSizes.A4;
                      const pageWidthMM = orientation === 'portrait' ? dimensions.width : dimensions.height;
                      const marginLeftMM = margins.left || 20;
                      const marginRightMM = margins.right || 20;
                      const contentWidthMM = pageWidthMM - marginLeftMM - marginRightMM;
                      
                      // Convert mm to pixels (96 DPI: 1 inch = 25.4mm = 96px)
                      const contentWidthPX = Math.round(contentWidthMM * 96 / 25.4);
                      
                      return `${contentWidthPX}px`;
                    })(),
                  },
                  '& .ql-container': {
                    backgroundColor: `${theme.palette.background.paper} !important`,
                    borderColor: `${theme.palette.divider} !important`,
                    color: `${theme.palette.text.primary} !important`,
                    height: 'auto',
                    minHeight: '500px',
                    border: 'none',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.05)',
                    position: 'relative',
                    '&::before': {
                      content: '"Page boundaries are indicated by subtle lines"',
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                      padding: '4px 8px',
                      fontSize: '11px',
                      borderRadius: '4px',
                      zIndex: 10,
                      pointerEvents: 'none',
                      opacity: 0.7,
                    }
                  },
                  '& .ql-editor': {
                    backgroundColor: `${theme.palette.background.paper} !important`,
                    color: `${theme.palette.text.primary} !important`,
                    minHeight: (() => {
                      // Calculate editor height based on page settings
                      const pageSizes = {
                        A4: { width: 210, height: 297 },
                        Letter: { width: 216, height: 279 },
                        Legal: { width: 216, height: 356 }
                      };
                      const pageSize = formData.config?.pageSize || 'A4';
                      const orientation = formData.config?.orientation || 'portrait';
                      const margins = formData.config?.margins || {};
                      
                      const dimensions = pageSizes[pageSize] || pageSizes.A4;
                      const pageHeightMM = orientation === 'portrait' ? dimensions.height : dimensions.width;
                      const marginTopMM = margins.top || 20;
                      const marginBottomMM = margins.bottom || 20;
                      const contentHeightMM = pageHeightMM - marginTopMM - marginBottomMM;
                      
                      // Convert mm to pixels (96 DPI: 1 inch = 25.4mm = 96px)
                      const contentHeightPX = Math.round(contentHeightMM * 96 / 25.4);
                      
                      return `${contentHeightPX}px`;
                    })(),
                    paddingBottom: '100px', // Add extra padding at bottom for smooth scrolling
                    position: 'relative',
                    // Removed backgroundImage gradient that was creating gray horizontal lines at page boundaries
                  },
                    '& .ql-editor p': {
                      color: theme.palette.text.primary,
                    },
                    '& .ql-editor h1, & .ql-editor h2': {
                      color: theme.palette.text.primary,
                    },
                    '& .ql-editor h3, & .ql-editor h4': {
                      color: theme.palette.text.primary,
                    },
                    '& .ql-editor h5, & .ql-editor h6': {
                      color: theme.palette.text.secondary,
                    },
                    '& .ql-editor blockquote': {
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      color: theme.palette.text.secondary,
                      borderLeftColor: theme.palette.info.main,
                    },
                    '& .ql-editor blockquote:before': {
                      color: theme.palette.info.main,
                    },
                    '& .ql-editor blockquote p': {
                      color: theme.palette.text.secondary,
                    },
                    '& .ql-editor pre': {
                      backgroundColor: theme.palette.action.hover,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    },
                    '& .ql-editor code': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.divider,
                    },
                    '& .ql-editor a': {
                      color: theme.palette.primary.main,
                    },
                    '& .ql-editor a:hover': {
                      borderBottomColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    '& .ql-editor::selection, & .ql-editor *::selection': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.contrastText,
                    },
                  }}
                >
                  {editorMode === 'visual' ? (
                    <>
                      {showPreservedWarning && (
                        <Alert 
                          severity="warning" 
                          sx={{ mb: 2 }}
                          onClose={() => {
                            setShowPreservedWarning(false);
                            setIsPreservedMode(false);
                            setPreservedHtml(null);
                          }}
                        >
                          <Typography variant="subtitle2" gutterBottom>
                            Complex HTML Content Detected
                          </Typography>
                          <Typography variant="body2">
                            The pasted content contains complex HTML structure and styles that cannot be displayed in the visual editor. 
                            Use the HTML editor to edit this content, or close this warning to convert it to a simpler format.
                          </Typography>
                        </Alert>
                      )}
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={isPreservedMode ? '' : formData.content}
                        onChange={handleContentChange}
                        modules={modules}
                        formats={formats}
                        className="custom-styled-editor"
                        readOnly={isPreservedMode}
                      />
                    </>
                  ) : (
                    <AceEditor
                      mode="html"
                      theme={theme.palette.mode === 'dark' ? 'monokai' : 'github'}
                      value={htmlContent}
                      onChange={handleHtmlChange}
                      onPaste={(pastedText) => {
                        if (!pastedText) return true;
                        
                        const htmlType = detectHtmlType(pastedText);
                        const editor = aceEditorRef.current?.editor;
                        
                        if (!editor) return true;
                        
                        // Handle based on content type and options
                        if (htmlType === 'plain-text') {
                          return true; // Let default paste handle plain text
                        }
                        
                        let processedContent = pastedText;
                        let shouldExtractStyles = false;
                        
                        // Handle full HTML documents
                        if (htmlType === 'full-document' || htmlType === 'styled-content') {
                          // If preserving formatting, we need to keep styles with content
                          if (pasteOptions.preserveFormatting) {
                            // Extract and inject styles into the content itself
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(pastedText, 'text/html');
                            
                            // Get all styles
                            const styleTags = doc.querySelectorAll('style');
                            let styleContent = '';
                            styleTags.forEach(tag => {
                              styleContent += tag.outerHTML + '\n';
                            });
                            
                            // Get body content with all attributes preserved
                            const bodyContent = doc.body ? doc.body.innerHTML : pastedText;
                            
                            // Combine styles with body content
                            processedContent = styleContent + bodyContent;
                            
                            // Also extract styles to CSS if option is enabled
                            if (pasteOptions.extractStyles) {
                              shouldExtractStyles = true;
                            }
                          } else {
                            // Not preserving formatting, extract body only
                            processedContent = extractBodyContent(pastedText);
                            shouldExtractStyles = pasteOptions.extractStyles;
                          }
                          
                          // Extract styles to CSS tab if needed
                          if (shouldExtractStyles) {
                            const extractedStyles = extractStylesFromHtml(pastedText);
                            if (extractedStyles) {
                              const currentCSS = formData.customCSS || '';
                              const newCSS = currentCSS + '\n\n/* Extracted from pasted HTML */\n' + extractedStyles;
                              handleChange('customCSS', newCSS);
                              setHasExtractedStyles(true);
                              setTimeout(() => setHasExtractedStyles(false), 5000);
                            }
                          }
                        }
                        
                        // Process for page constraints if enabled
                        if (pasteOptions.processForPages && !pasteOptions.preserveFormatting) {
                          const pageHeight = pageManager?.maxContentHeightPx || 971;
                          processedContent = processHtmlForPages(processedContent, pageHeight);
                        }
                        
                        // Format and insert
                        const formatted = pasteOptions.preserveFormatting ? processedContent : formatHtml(processedContent);
                        const position = editor.getCursorPosition();
                        editor.session.insert(position, formatted);
                        
                        // Update the main content immediately to ensure styles are applied
                        if (pasteOptions.preserveFormatting) {
                          const currentContent = editor.getValue();
                          const restoredHtml = restorePageBreaksFromHtml(currentContent);
                          handleChange('content', restoredHtml);
                          // Store the preserved HTML and mark as preserved mode
                          if (htmlType === 'full-document' || htmlType === 'styled-content') {
                            setPreservedHtml(restoredHtml);
                            setIsPreservedMode(true);
                          }
                        }
                        
                        return false; // Prevent default paste
                      }}
                      ref={aceEditorRef}
                      name="html-editor"
                      width="100%"
                      height="100%"
                      fontSize={14}
                      showPrintMargin={false}
                      showGutter={true}
                      highlightActiveLine={true}
                      setOptions={{
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: true,
                        enableSnippets: true,
                        showLineNumbers: true,
                        tabSize: 2,
                        useWorker: false,
                        wrap: true,
                        enableEmmet: true,
                      }}
                      editorProps={{ $blockScrolling: true }}
                      style={{
                        fontFamily: 'Monaco, Menlo, "Courier New", monospace'
                      }}
                      commands={[
                        {
                          name: 'formatHTML',
                          bindKey: { win: 'Shift-Alt-F', mac: 'Shift-Alt-F' },
                          exec: () => {
                            const formatted = formatHtml(htmlContent);
                            setHtmlContent(formatted);
                            const restoredHtml = restorePageBreaksFromHtml(formatted);
                            handleChange('content', restoredHtml);
                          }
                        },
                        {
                          name: 'processForPages',
                          bindKey: { win: 'Ctrl-Alt-P', mac: 'Cmd-Alt-P' },
                          exec: () => {
                            const pageHeight = pageManager?.maxContentHeightPx || 971;
                            const processed = processHtmlForPages(htmlContent, pageHeight);
                            const formatted = formatHtml(processed);
                            setHtmlContent(formatted);
                            const restoredHtml = restorePageBreaksFromHtml(formatted);
                            handleChange('content', restoredHtml);
                          }
                        }
                      ]}
                    />
                  )}
                </Box>
            </Box>
          
          <Box sx={{ display: activeTab === 1 ? 'block' : 'none', height: '100%' }}>
            <PDFCustomCode
              customCSS={formData.customCSS}
              customJS={formData.customJS}
              jsEnabled={formData.config.jsEnabled}
              onCSSChange={(css) => handleChange('customCSS', css)}
              onJSChange={(js) => handleChange('customJS', js)}
              onJSEnabledChange={(enabled) => handleConfigChange('jsEnabled', enabled)}
            />
          </Box>
          
          <Box sx={{ display: activeTab === 2 ? 'block' : 'none', height: '100%' }}>
            <PDFSettings
              config={formData.config}
              isPublic={formData.isPublic}
              tags={formData.tags}
              slug={formData.slug}
              onConfigChange={handleConfigChange}
              onPublicChange={(value) => handleChange('isPublic', value)}
              onSlugChange={(value) => handleChange('slug', value)}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
            />
          </Box>
        </Box>
        
        {/* Preview */}
        {(viewMode === 'split' || (viewMode === 'toggle' && showPreview)) && (
          <>
            <Divider orientation="vertical" />
            <Box sx={{ 
              flex: 1,
              overflow: 'hidden',
              bgcolor: 'background.paper'
            }}>
              <PDFPreview
                content={isPreservedMode && preservedHtml ? preservedHtml : formData.content}
                customCSS={formData.customCSS}
                customJS={formData.config.jsEnabled ? formData.customJS : ''}
                config={formData.config}
                title={formData.title}
                autoRefresh={formData.config.preview.autoRefresh}
                currentEditorPage={currentPageInfo.currentPage}
                cursorPosition={currentPageInfo.cursorPosition}
              />
            </Box>
          </>
        )}
      </Box>
      
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={generating}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={handleCancelNavigation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Would you like to save them before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDiscardAndNavigate} 
            color="error"
          >
            Discard Changes
          </Button>
          <Button 
            onClick={handleCancelNavigation}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAndNavigate}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Save Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PDFEditor;